import { memo, FC, useCallback, useMemo, useState } from 'react'
import { GroupChatTab } from './components/groupChatTab/groupChatTab'
import { Route, Routes, useParams } from 'react-router-dom'
import { CrewErrorBoundary } from 'components/functions/crewErrorBoundary'
import { GroupChatTabs, SearchTabs } from 'enums/app'
import { GroupChatChatPanel } from './components/groupChatChatPanel/groupChatChatPanel'
import { GroupChatHeadPanel } from './components/groupChatHeadPanel/groupChatHeadPanel'
import { GroupChatFileList } from './components/groupChatFileList/groupChatFileList'
import { CrewTextBox } from 'components/devextreme/crewTextBox'
import { TextBoxInstance } from 'devextreme/ui/text_box'
import { NativeEventInfo } from 'devextreme/events'
import { OPERATION_KEY } from 'configs/constants'
import { useCrewNavigate } from 'hooks/useCrewNavigate'
import { useTranslation } from '@crew/modules/i18n'
import { Button as TextBoxButton } from 'devextreme-react/text-box'
import { ButtonTypes } from 'devextreme-react/cjs/button'
import { KeywordFilterCondition, ProjectType } from '@crew/enums/domain'
import { getDefaultTabValue } from '@crew/utils/enum'
import { GroupChatMemberList } from './components/groupChatMemberList/groupChatMemberList'
import { ProjectGroupRef, UserRef } from '@crew/models/refs'
import { Favorite } from '@crew/models/domain'

export type GroupChat = {
  id: string
  subject: string
  description: string | null
  scope: string
  approvalToJoin: boolean
  ownerUser: UserRef
  projectGroup: ProjectGroupRef
  projectType: ProjectType
  projectMembers: UserRef[]
  projectFavorite: Favorite | null
  archived: boolean
  version: number
}

export type GroupChatPanelProps = {
  groupChat: GroupChat | null | undefined
  isLoadingGroupChat: boolean
  isErrorGroupChat: boolean
  isJoinedGroupChat: boolean
  onDeletedGroupChat: () => void
}

export const GroupChatPanel: FC<GroupChatPanelProps> = memo((props) => {
  const { t } = useTranslation()
  const { navigate } = useCrewNavigate()
  const { projectId } = useParams()

  // 検索モードの初期値を設定
  const [searchConditionMode, setSearchConditionMode] =
    useState<KeywordFilterCondition>('and')

  // 検索ボックスでEnterキーが押されたときに発火するイベントハンドラ
  const handleCrossSearchChanged = useCallback(
    (e: NativeEventInfo<TextBoxInstance, KeyboardEvent>) => {
      const keyword = e.component.option('text')
      keyword &&
        navigate(`/search/${getDefaultTabValue(SearchTabs)}`, {
          keyword,
          projectId,
          [OPERATION_KEY]: searchConditionMode,
        })
    },
    [navigate, projectId, searchConditionMode]
  )

  // 検索ボックス内のモード切替（and <-> or）ボタンの設定
  const searchConditionButton = useMemo<ButtonTypes.Properties>(
    () => ({
      stylingMode: 'text',
      onClick: () => {
        setSearchConditionMode((prevConditionMode: KeywordFilterCondition) =>
          prevConditionMode === 'and' ? 'or' : 'and'
        )
      },
      text: searchConditionMode.toLocaleUpperCase(),
    }),
    [searchConditionMode]
  )

  return (
    <div className="flex flex-1 gap-2 grow min-h-0">
      <main className="grow flex flex-col h-full w-2/3 overflow-y-auto">
        <CrewErrorBoundary>
          {/* グループチャットヘッダーパネル */}
          <GroupChatHeadPanel
            groupChat={props.groupChat}
            isLoadingGroupChat={props.isLoadingGroupChat}
            isErrorGroupChat={props.isErrorGroupChat}
            isJoinedGroupChat={props.isJoinedGroupChat}
            onDeletedGroupChat={props.onDeletedGroupChat}
          />
        </CrewErrorBoundary>
        <CrewErrorBoundary>
          <div className="min-h-0 flex-1 flex flex-col grow w-full">
            <div className="px-2">
              <div className="flex flex-row border-b crew-border-gray items-center">
                {/* グループチャットタブ */}
                <GroupChatTab />

                {/* グループ内横断検索ボックス */}
                <div className="pr-0.5 ml-auto">
                  <CrewTextBox
                    id="search"
                    name="search"
                    placeholder={t('action.search')}
                    mode="search"
                    onEnterKey={handleCrossSearchChanged}
                  >
                    <TextBoxButton
                      name="searchCondition"
                      location="after"
                      options={searchConditionButton}
                    />
                  </CrewTextBox>
                </div>
              </div>
            </div>
            <div className="grow min-h-0 w-full flex flex-col">
              {/* URLに応じて表示するコンポーネントを切り替える */}
              <Routes>
                {/* チャット */}
                {/* 
                  TODO: 一時的にデフォルトルート（index）を除去している
                  以下タスク対応時にデフォルトルートを設定する予定
                  https://break-tmc.atlassian.net/browse/CREW-9163
                */}
                <Route
                  path={GroupChatTabs.Chat.value}
                  element={<GroupChatChatPanel />}
                />

                {/* ファイル */}
                <Route
                  path={GroupChatTabs.File.value}
                  element={<GroupChatFileList />}
                />

                {/* メンバー */}
                <Route
                  path={GroupChatTabs.Member.value}
                  element={<GroupChatMemberList />}
                />
              </Routes>
            </div>
          </div>
        </CrewErrorBoundary>
      </main>
    </div>
  )
})
